import {
    Component,
    ElementRef,
    HostListener,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { LayoutService } from '../services/layout.service';
import { primaryColors, surfaceColors } from '../constant/color-scheme.constant';

// ------------------------
// COMPONENT
// ------------------------
/**
 * AppConfigPanelComponent — floating config panel for theme and layout customization.
 * Allows user to change primary color, surface color.
 */
@Component({
    standalone: false,
    selector: 'app-config-panel',
    templateUrl: './admin-config.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './admin-config.component.scss',
})
export class AppConfigPanelComponent {
    // ------------------------
    // VIEW REFERENCES
    // ------------------------
    // Direct reference to the config panel DOM element (used for outside click detection)
    @ViewChild('panel') panel!: ElementRef;

    // ------------------------
    // COLOR DATA
    // ------------------------
    // List of available primary colors for the color picker
    primaryColors = primaryColors;

    // List of available surface colors for the color picker
    surfaceColors = surfaceColors;

    // ------------------------
    // CONSTRUCTOR
    // ------------------------
    // LayoutService injected as public so template can access it directly
    constructor(public layoutService: LayoutService) {}

    // ------------------------
    // HOST LISTENER
    // ------------------------
    // Listens to clicks anywhere on the document
    // Closes config panel if user clicks outside the panel
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Check if click was inside the config panel
        const clickedInsidePanel = this.panel?.nativeElement.contains(target);

        // Close panel only if click is outside the panel
        if (!clickedInsidePanel) {
            this.layoutService.closeConfigPanel();
        }
    }

    // ------------------------
    // COLOR SELECTION
    // ------------------------
    // Sets the selected primary color in layout config
    selectPrimary(colorName: string): void {
        this.layoutService.setPrimaryColor(colorName);
    }

    // Sets the selected surface color in layout config
    selectSurface(colorName: string): void {
        this.layoutService.setSurfaceColor(colorName);
    }

    // ------------------------
    // GETTERS
    // ------------------------
    // Returns currently active primary color name from layout config
    get currentPrimary(): string {
        return this.layoutService.getPrimary();
    }

    // Returns currently active surface color name from layout config (can be null)
    get currentSurface(): string | null | undefined {
        return this.layoutService.getSurface();
    }

    // ------------------------
    // COLOR VALUE HELPERS
    // ------------------------
    // Returns hex value of a primary color by name, defaults to black if not found
    getPrimaryColorValue(name: string): string {
        return primaryColors.find((c) => c.name === name)?.value || '#000';
    }

    // Returns hex value of a surface color by name, defaults to black if not found
    getSurfaceColorValue(name: string): string {
        return surfaceColors.find((c) => c.name === name)?.value || '#000';
    }
}
